/**
 * Super Admin — AI token usage.
 *
 * Volume only: who is using the assistant heavily, and which workspace they had
 * open. No prices (a rate table goes stale silently) and no anomaly flag (a
 * threshold on a dataset this size is a guess, and one false alarm costs trust
 * in the whole page). Ranking plus each row's share is honest and legible.
 *
 * Reads two SECURITY DEFINER RPCs that check is_platform_master() themselves, so
 * there is no endpoint in front of this — the database is the boundary.
 *
 * @module pages/admin/AiUsage
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldAlert, RefreshCw } from "lucide-react";
import { YDMPageHero } from "../../components/brand";
import { listAiUsageByUser, listAiUsageByWorkspace, type AiUsageRow } from "../../services/aiUsageService";
import { AiUsagePie } from "../../components/admin/AiUsagePie";

type Dimension = "user" | "workspace";
const RANGES = [7, 30, 90] as const;

const formatWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const Tile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white rounded-lg border border-neutral-200 px-4 py-3">
    <p className="text-xs text-neutral-700">{label}</p>
    <p className="text-lg font-semibold text-ink-900">{value}</p>
  </div>
);

const AdminAiUsage: React.FC = () => {
  const [userRows, setUserRows] = useState<AiUsageRow[]>([]);
  const [wsRows, setWsRows] = useState<AiUsageRow[]>([]);
  const [days, setDays] = useState<number>(30);
  const [dimension, setDimension] = useState<Dimension>("user");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Both dimensions are fetched together: the two pies are always on screen, and
  // the toggle now only picks which one the table shows — so it must not refetch.
  const load = useCallback(async () => {
    setLoading(true);
    const [byUser, byWorkspace] = await Promise.all([listAiUsageByUser(days), listAiUsageByWorkspace(days)]);
    if (byUser.success && byWorkspace.success) {
      setUserRows(byUser.data ?? []);
      setWsRows(byWorkspace.data ?? []);
      setError(null);
    } else {
      // Both read the same table through the same master gate, so one failing
      // alone is unlikely — and rendering half the picture without saying so
      // would be worse than showing nothing.
      setError(byUser.error ?? byWorkspace.error ?? "Failed to load AI usage");
    }
    setLoading(false);
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = dimension === "user" ? userRows : wsRows;
  const windowTotal = rows.reduce((sum, r) => sum + r.totalTokens, 0);
  const totalCalls = rows.reduce((sum, r) => sum + r.calls, 0);
  // Guarded: an empty window would otherwise print NaN% in every row.
  const share = (n: number): string => (windowTotal > 0 ? `${((n / windowTotal) * 100).toFixed(1)}%` : "—");

  const hero = (
    <YDMPageHero
      eyebrow="Super Admin"
      headline="AI USAGE"
      subtitle="ปริมาณการใช้ token ของผู้ช่วย AI"
      actions={
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-ink-950 text-white hover:bg-ink-900 disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : undefined} />
          Refresh
        </button>
      }
    />
  );

  if (error) {
    return (
      <div className="space-y-4 animate-fade-in">
        {hero}
        <div className="bg-white rounded-lg border border-neutral-200 p-8 text-center">
          <ShieldAlert className="w-12 h-12 text-yellow-700 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-ink-900 mb-2">This area is for the platform master</h2>
          <p className="text-neutral-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {hero}

      <div className="flex flex-wrap gap-2">
        {RANGES.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
              days === d ? "bg-ink-950 text-white border-ink-950" : "border-neutral-300 hover:bg-neutral-50"
            }`}
          >
            {d} วัน
          </button>
        ))}
        <span className="w-px bg-neutral-200 mx-1" aria-hidden="true" />
        {(["user", "workspace"] as const).map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDimension(d)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
              dimension === d ? "bg-ink-950 text-white border-ink-950" : "border-neutral-300 hover:bg-neutral-50"
            }`}
          >
            {d === "user" ? "รายผู้ใช้" : "ราย workspace"}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Tile label="Total tokens" value={windowTotal.toLocaleString()} />
        <Tile label="Calls" value={totalCalls.toLocaleString()} />
        {/* The RPC returns the unattributed group as a row on purpose, so the
            totals reconcile — but a count of workspaces must not include it.
            (user_id is NOT NULL, so the user dimension has no such row.) */}
        <Tile
          label={dimension === "user" ? "Users" : "Workspaces"}
          value={String(rows.filter((r) => r.key !== null).length)}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <AiUsagePie title="สัดส่วน token รายผู้ใช้" rows={userRows} unit="คน" />
        <AiUsagePie title="สัดส่วน token ราย workspace" rows={wsRows} unit="workspace" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-ink-900 animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-neutral-700">
              <tr>
                <th className="px-4 py-3 font-medium">{dimension === "user" ? "ผู้ใช้" : "Workspace"}</th>
                <th className="px-4 py-3 font-medium">สัดส่วน</th>
                <th className="px-4 py-3 font-medium">Calls</th>
                <th className="px-4 py-3 font-medium">Total tokens</th>
                <th className="px-4 py-3 font-medium">Thinking</th>
                <th className="px-4 py-3 font-medium">เฉลี่ย/call</th>
                {dimension === "workspace" && <th className="px-4 py-3 font-medium">คน</th>}
                <th className="px-4 py-3 font-medium">ล่าสุด</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.length === 0 && (
                <tr>
                  {/* The "คน" column only exists in the workspace dimension. */}
                  <td colSpan={dimension === "workspace" ? 8 : 7} className="px-4 py-8 text-center text-neutral-700">
                    ยังไม่มีการเรียกใช้ AI ในช่วงนี้
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.key ?? "unattributed"}>
                  <td className="px-4 py-3">{r.label}</td>
                  <td className="px-4 py-3">{share(r.totalTokens)}</td>
                  <td className="px-4 py-3">{r.calls.toLocaleString()}</td>
                  <td className="px-4 py-3">{r.totalTokens.toLocaleString()}</td>
                  <td className="px-4 py-3">{r.thinkingTokens.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    {r.calls > 0 ? Math.round(r.totalTokens / r.calls).toLocaleString() : "—"}
                  </td>
                  {dimension === "workspace" && <td className="px-4 py-3">{r.users ?? "—"}</td>}
                  <td className="px-4 py-3 whitespace-nowrap">{formatWhen(r.lastUsed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Always shown, not gated on the toggle: the workspace pie is on screen in
          both dimensions, so the caveat that qualifies it must be too. */}
      <p className="text-xs text-neutral-700">
        ตัวเลข workspace คือ workspace ที่ผู้ใช้เปิดอยู่ตอนเรียก ไม่ใช่ยอดรวมของลูกค้ารายนั้น —
        คนเดียวที่สลับ workspace จะถูกแยกเป็นหลายแถว
      </p>

      <p className="text-xs text-neutral-700">
        เก็บย้อนหลังได้ 90 วัน และเริ่มบันทึกตั้งแต่ฟีเจอร์นี้ขึ้นเท่านั้น —
        ช่วงก่อนหน้าจะว่างเพราะยังไม่ได้เก็บ ไม่ใช่เพราะไม่มีใครใช้
      </p>
    </div>
  );
};

export default AdminAiUsage;
